/**
 * Planet surface shader.
 *
 * One material branching on a `uType` uniform rather than ten separate shaders.
 * Uniform branching is uniform across the whole draw, so the cost is a
 * predicted jump, and it means lighting, the terminator and the atmospheric rim
 * are written once instead of ten times and drifting apart.
 *
 * The map deliberately runs this at low quality. A player should be able to see
 * that a world has oceans, not count its islands from three systems away —
 * leaving something to find is the point of flying there.
 *
 * Type governs pattern only. All colour comes from the planet's own palette, so
 * a jungle world can be violet and an ocean world orange.
 */

import * as THREE from 'three';
import type { PlanetType } from '@interspacial/shared';

import { NOISE_GLSL } from './noise.glsl';

/** Must match the `uType` branch order in the fragment shader. */
const TYPE_INDEX: Record<PlanetType, number> = {
  ocean: 0,
  islandic: 1,
  jungle: 2,
  desert: 3,
  rocky: 4,
  barren: 5,
  ice: 6,
  volcanic: 7,
  toxic: 8,
  gasGiant: 9,
};

const vertexShader = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_vertex>

  varying vec3 vLocal;
  varying vec3 vNormalW;
  varying vec3 vWorld;

  void main() {
    vLocal = position;
    vNormalW = normalize(mat3(modelMatrix) * normal);

    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    #include <logdepthbuf_vertex>
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;
  #include <common>
  #include <logdepthbuf_pars_fragment>

  ${NOISE_GLSL}

  varying vec3 vLocal;
  varying vec3 vNormalW;
  varying vec3 vWorld;

  uniform vec3 uBase;
  uniform vec3 uAccent;
  uniform vec3 uHighlight;
  uniform vec3 uLightPos;
  uniform vec3 uLightColor;
  uniform float uSeed;
  uniform float uTime;
  uniform int uType;
  uniform int uOctaves;
  /** 0 means no ice caps at all; higher reaches further toward the equator. */
  uniform float uCapExtent;

  /** Latitude 0 at the equator, 1 at either pole. */
  float polar(vec3 dir) {
    return abs(dir.z);
  }

  /**
   * Ice cap coverage at this point.
   *
   * A cap taken straight from latitude is a hard-edged disc pasted on the pole,
   * which is exactly what it looks like — the artefact that made planets seem
   * to break at top and bottom. Perturbing the boundary with the same noise
   * that drives the surface gives it a ragged edge, and uCapExtent lets some
   * worlds have none at all.
   */
  float iceCap(vec3 dir, vec3 p, float extent) {
    if (extent <= 0.0) return 0.0;
    float ragged = fbm(p * 3.0 + 19.0, 3) - 0.5;
    float latitude = polar(dir) + ragged * 0.22;
    float edge = 1.0 - extent;
    return smoothstep(edge, min(edge + 0.28, 0.99), latitude);
  }

  void main() {
    vec3 dir = normalize(vLocal);
    vec3 p = dir * 2.2 + uSeed;
    vec3 surface = uBase;

    float land = warped(p, uOctaves, 1.2);
    float detail = fbm(p * 4.3, max(uOctaves - 1, 2));
    float caps = iceCap(dir, p, uCapExtent);

    if (uType == 0) {
      // Ocean — mostly water, a few big continents, bright shelves at the coast.
      float coast = smoothstep(0.52, 0.58, land);
      float shelf = smoothstep(0.46, 0.52, land) * (1.0 - coast);
      surface = mix(uBase, uHighlight, shelf * 0.5);
      surface = mix(surface, uAccent, coast);
    } else if (uType == 1) {
      // Islandic — the same idea broken into archipelagos by higher frequency.
      float speckle = warped(p * 3.1, uOctaves, 1.6);
      float isles = smoothstep(0.55, 0.62, land * 0.5 + speckle * 0.5);
      surface = mix(uBase, uAccent, isles);
      surface = mix(surface, uHighlight, smoothstep(0.5, 0.56, speckle) * 0.25);
    } else if (uType == 2) {
      // Jungle — land dominates and is mottled with dense growth.
      float growth = smoothstep(0.4, 0.75, land);
      surface = mix(uBase, uAccent, growth);
      surface = mix(surface, uAccent * 0.6, detail * 0.5);
    } else if (uType == 3) {
      // Desert — banded dunes, which is mostly a stretched noise field.
      float dunes = fbm(vec3(p.x * 1.4, p.y * 1.4, p.z * 7.0), uOctaves);
      surface = mix(uBase, uAccent, smoothstep(0.42, 0.68, dunes));
      surface = mix(surface, uHighlight, smoothstep(0.7, 0.9, dunes) * 0.3);
    } else if (uType == 4) {
      // Rocky — ridged uplands over a mottled base.
      float ridges = ridged(p * 1.8, uOctaves);
      surface = mix(uBase, uAccent, smoothstep(0.45, 0.8, ridges));
      surface *= 0.85 + 0.3 * detail;
    } else if (uType == 5) {
      // Barren — nearly featureless, pocked with craters.
      float craters = 1.0 - smoothstep(0.0, 0.28, abs(valueNoise(p * 5.5) - 0.5));
      surface = mix(uBase, uAccent, detail * 0.35);
      surface *= 1.0 - craters * 0.35;
    } else if (uType == 6) {
      // Ice — pale and fractured, with the caps reaching furthest.
      float cracks = 1.0 - smoothstep(0.0, 0.06, abs(ridged(p * 2.4, uOctaves) - 0.55));
      surface = mix(uBase, uHighlight, 0.45 + detail * 0.25);
      surface = mix(surface, uAccent, cracks * 0.5);
    } else if (uType == 7) {
      // Volcanic — dark crust split by glowing fissures. The glow is added
      // after lighting so it survives on the night side.
      float crust = smoothstep(0.35, 0.7, land);
      surface = mix(uBase * 0.4, uBase, crust);
      surface = mix(surface, uAccent * 0.5, detail * 0.4);
    } else if (uType == 8) {
      // Toxic — thick swirling cloud with no visible ground at all.
      float swirl = warped(p * 1.6 + vec3(0.0, 0.0, uTime * 0.01), uOctaves, 2.2);
      surface = mix(uBase, uAccent, smoothstep(0.35, 0.7, swirl));
      surface = mix(surface, uHighlight, smoothstep(0.72, 0.9, swirl) * 0.45);
    } else {
      // Gas giant — latitude bands warped into turbulence, plus a storm cell.
      float bands = sin(dir.z * 9.0 + fbm(p * 1.2, uOctaves) * 5.0);
      surface = mix(uBase, uAccent, smoothstep(-0.25, 0.35, bands));
      float storm = smoothstep(0.86, 0.97, warped(p * 1.9, uOctaves, 1.8));
      surface = mix(surface, uHighlight, storm * 0.7);
    }

    // Caps only where they make sense — a gas giant has no surface to freeze,
    // and a volcanic or toxic world is not keeping ice. Everything else is
    // gated by uCapExtent, which is zero for many worlds.
    if (uType != 9 && uType != 7 && uType != 8) {
      surface = mix(surface, uHighlight, caps * 0.8);
    }

    // ── Lighting ────────────────────────────────────────────────────────────
    vec3 normal = normalize(vNormalW);
    vec3 toLight = normalize(uLightPos - vWorld);

    // Wrapped diffuse: a hard terminator on a body this small reads as a bite
    // taken out of it, so the light is allowed to bend a little past the edge.
    float lambert = dot(normal, toLight);
    float day = clamp((lambert + 0.35) / 1.35, 0.0, 1.0);

    vec3 lit = surface * uLightColor * (0.12 + 0.98 * day);

    // Volcanic fissures glow through the night side.
    if (uType == 7) {
      float fissure = 1.0 - smoothstep(0.0, 0.05, abs(ridged(p * 2.2, uOctaves) - 0.62));
      lit += uHighlight * fissure * (1.4 - day * 0.7);
    }

    // Atmospheric rim, strongest where the light grazes the limb.
    float rim = pow(1.0 - abs(dot(normal, normalize(cameraPosition - vWorld))), 3.0);
    lit += uAccent * rim * day * 0.35;

    gl_FragColor = vec4(lit, 1.0);
    #include <logdepthbuf_fragment>
  }
`;

export interface PlanetMaterialOptions {
  type: PlanetType;
  baseColor: THREE.ColorRepresentation;
  accentColor: THREE.ColorRepresentation;
  highlightColor: THREE.ColorRepresentation;
  seed: number;
  /** Where the system's star is, in world space. */
  lightPosition: THREE.Vector3;
  lightColor: THREE.ColorRepresentation;
  /** 0..0.4 — how far ice caps reach. Many worlds should be 0. */
  capExtent: number;
  quality?: 'low' | 'high';
}

export function createPlanetMaterial(options: PlanetMaterialOptions): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uBase: { value: new THREE.Color(options.baseColor) },
      uAccent: { value: new THREE.Color(options.accentColor) },
      uHighlight: { value: new THREE.Color(options.highlightColor) },
      uLightPos: { value: options.lightPosition.clone() },
      uLightColor: { value: new THREE.Color(options.lightColor) },
      uSeed: { value: (options.seed % 997) * 0.031 },
      uTime: { value: 0 },
      uType: { value: TYPE_INDEX[options.type] },
      uOctaves: { value: options.quality === 'high' ? 6 : 3 },
      uCapExtent: { value: options.capExtent },
    },
  });
}
